import { ResourcePage } from "@/components/ResourcePage";
import { Calendar, Download, ExternalLink, Bell } from "lucide-react";
import useReveal from "@/lib/useReveal";

export function SubscribeToTaxCalendar() {
  useReveal();
  return (
    <ResourcePage title="Subscribe to Tax Calendar" testId="page-subscribe-tax-calendar">
      <div className="max-w-3xl space-y-5 text-navy-600 text-[16px] leading-relaxed reveal">
        <p>Never miss a federal filing deadline. Subscribe the IRS Tax Calendar directly to your phone or computer, and deadlines and reminders will appear automatically alongside your existing events.</p>
        <h2 className="font-serif-display text-2xl text-navy-800 pt-2">Option 1: IRS Online Tax Calendar</h2>
        <p>The IRS publishes an interactive online tax calendar with key dates for individuals, employers, and businesses.</p>
        <a href="https://www.tax.gov/calendar/" target="_blank" rel="noopener noreferrer" className="btn-primary inline-flex" data-testid="taxcal-irs">
          Open the IRS Tax Calendar <ExternalLink size={14} />
        </a>

        <h2 className="font-serif-display text-2xl text-navy-800 pt-4">Option 2: Subscribe (.ics)</h2>
        <p>To add the IRS Tax Calendar directly to Outlook, Google Calendar, or Apple Calendar, copy the subscribe URL below into your calendar app's "Add subscribed calendar" feature:</p>
        <div className="rounded border border-navy-100 bg-cream-100/40 px-4 py-3 font-mono text-[13.5px] text-navy-800 break-all" data-testid="taxcal-ics-url">
          https://www.tax.gov/calendar/subscribe/general
        </div>
        <ul className="list-disc pl-6 space-y-2 text-[14.5px]">
          <li><strong>Apple Calendar:</strong> File → New Calendar Subscription → paste URL.</li>
          <li><strong>Google Calendar:</strong> Other calendars → "+" → From URL → paste URL.</li>
          <li><strong>Outlook:</strong> Calendar → Add Calendar → Subscribe from web → paste URL.</li>
        </ul>

        <h2 className="font-serif-display text-2xl text-navy-800 pt-4">Want a personalised reminder list?</h2>
        <p>Send us a quick note about which deadlines apply to your situation (individual, S-corp, partnership, payroll) and we'll prepare a tailored calendar for you.</p>
        <a href="/contact" className="btn-secondary inline-flex" data-testid="taxcal-contact">
          <Bell size={14} /> Request a personalised calendar
        </a>
      </div>
    </ResourcePage>
  );
}

export function TaxAppointmentChecklistPage() {
  useReveal();
  const items = [
    "Last year's federal and state tax returns",
    "All W-2 forms from each employer",
    "All 1099 forms (NEC, MISC, INT, DIV, B, R, K, SSA, G)",
    "Self-employment income & expense records",
    "Mortgage interest statement (Form 1098)",
    "Property tax statements",
    "Real estate sale closing statements",
    "Charitable contribution records (cash and non-cash)",
    "Medical, dental and vision expense receipts",
    "Childcare provider name, address, EIN/SSN, and amounts paid",
    "Tuition statements (Form 1098-T) and student loan interest (1098-E)",
    "K-1 statements from partnerships, S corps, trusts",
    "HSA contributions and distributions (Form 5498-SA / 1099-SA)",
    "IRA / 401(k) contributions and distributions (Form 5498 / 1099-R)",
    "Estimated tax payments (federal and state) with dates",
    "Social Security numbers and dates of birth for spouse and dependents",
    "Direct deposit info (routing & account number) for any refund",
    "Health insurance coverage forms (1095-A, B, or C)",
    "Records of energy-efficient home improvements (if claiming credits)",
    "Cryptocurrency purchase, sale, and transfer records",
  ];
  return (
    <ResourcePage title="Tax Appointment Checklist" testId="page-tax-appointment-checklist">
      <div className="max-w-3xl reveal">
        <p className="text-navy-600 text-[16px] leading-relaxed">Bring the following items to your tax appointment so we can prepare your return accurately and efficiently. Don't worry if you're missing something. We'll help you track it down.</p>
        <div role="note" className="mt-5 rounded border border-amber-200 bg-amber-50 px-4 py-3 text-amber-900 text-[13.5px] leading-relaxed">
          <strong>Please deliver sensitive items securely.</strong> Documents containing Social Security numbers, bank account/routing numbers, or signed tax returns should be brought in person, mailed, faxed, or shared through a secure channel we provide. Please do not send them by ordinary email or text message.
        </div>
        <a href="/files/Tax-Appointment-Checklist.doc" target="_blank" rel="noopener noreferrer" className="btn-primary inline-flex mt-6" data-testid="appt-checklist-download">
          <Download size={14} /> Download .doc version
        </a>
        <a href="https://outlook.office.com/book/StJohnFinancialGroup@stjohnfinancialgroup.com/" target="_blank" rel="noopener noreferrer" className="btn-secondary inline-flex mt-6 ml-3" data-testid="appt-checklist-book">
          <Calendar size={14} /> Book my appointment
        </a>

        <ul className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2.5">
          {items.map((it, i) => (
            <li key={i} className="flex gap-3 items-start text-navy-700 text-[14.5px] leading-snug">
              <span className="mt-[7px] h-1.5 w-1.5 rounded-full bg-gold-500 shrink-0" />
              {it}
            </li>
          ))}
        </ul>
      </div>
    </ResourcePage>
  );
}
